import { useState } from 'react';
import { Plus } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';

import { FormControl } from '@@/form-components/FormControl';
import { TextTip } from '@@/Tip/TextTip';
import { Button } from '@@/buttons';
import { FormSection } from '@@/form-components/FormSection';

import { RegistrySelector } from '../../RegistrySelector/RegistrySelector';
import { NamespaceSelect } from '../rbac/NamespaceSelect';

import { KubernetesRegistryAccessDatatable } from './KubernetesRegistryAccessDatatable';
import { KubernetesRegistryTypeSpecificData } from './types';

interface Props {
  formData: KubernetesRegistryTypeSpecificData;
  onChange: <K extends keyof KubernetesRegistryTypeSpecificData>(
    key: K,
    value: KubernetesRegistryTypeSpecificData[K]
  ) => void;
  mode: 'create' | 'edit';
}

export function KubernetesRegistryFormFields({
  formData,
  onChange,
  mode,
}: Props) {
  const [tempSelectedRegistry, setTempSelectedRegistry] = useState<
    number | null
  >(null);
  const [tempSelectedNamespaces, setTempSelectedNamespaces] = useState<
    string[]
  >([]);

  const canAddAccess =
    tempSelectedRegistry !== null && tempSelectedNamespaces.length > 0;

  return (
    <>
      <FormSection title="Add access">
        <RegistrySelector
          value={tempSelectedRegistry}
          onChange={setTempSelectedRegistry}
          filter={(registries) => registries.filter((r) => r.Id !== 0)}
          required
          isClearable
        />

        <FormControl label="Namespaces" required>
          <p className="mb-2 text-xs text-muted">
            Add the namespaces that should have access to the registry:
          </p>
          <NamespaceSelect
            selectedNamespaces={tempSelectedNamespaces}
            onChange={setTempSelectedNamespaces}
            placeholder="e.g. default, production"
            data-cy="registry-namespaces-input"
            namespaces={getNamespacesFromAccessList()}
          />
        </FormControl>

        <div className="form-group">
          <div className="col-sm-12 flex gap-2">
            <Button
              icon={Plus}
              color="primary"
              size="small"
              disabled={!canAddAccess}
              onClick={handleAddAccess}
              data-cy="add-registry-access-button"
              className="!ml-0"
            >
              Add Access
            </Button>
          </div>
        </div>
      </FormSection>

      <div className="-mx-4">
        <KubernetesRegistryAccessDatatable
          formData={{ accessRows: formData.accessRows }}
          onChange={onChange}
        />
      </div>

      <div className="form-group">
        <div className="col-sm-12">
          <RegistryAccessInfoMessage
            registriesAddedCount={formData.accessRows?.length ?? 0}
            mode={mode}
          />
        </div>
      </div>
    </>
  );

  function handleAddAccess() {
    if (!canAddAccess) return;

    const cleanedNamespaces = tempSelectedNamespaces
      .map((ns) => ns.trim())
      .filter((ns) => ns !== '');

    const newAccessRows = [...(formData.accessRows || [])];
    const existingIndex = newAccessRows.findIndex(
      (row) => row.registryId === tempSelectedRegistry
    );

    if (existingIndex !== -1) {
      newAccessRows[existingIndex] = {
        ...newAccessRows[existingIndex],
        namespaces: cleanedNamespaces,
      };
    } else {
      newAccessRows.push({
        registryId: tempSelectedRegistry as number,
        namespaces: cleanedNamespaces,
      });
    }

    onChange('accessRows', newAccessRows);
    setTempSelectedRegistry(null);
    setTempSelectedNamespaces([]);
  }

  function getNamespacesFromAccessList(): string[] {
    return Array.from(
      new Set((formData.accessRows || []).flatMap((a) => a.namespaces || []))
    );
  }
}

function RegistryAccessInfoMessage({
  registriesAddedCount,
  mode,
}: {
  registriesAddedCount: number;
  mode: 'create' | 'edit';
}) {
  return <TextTip color="blue">{getMessage()}</TextTip>;

  function getMessage() {
    if (registriesAddedCount > 0) {
      return `Registry access configured for ${registriesAddedCount} ${pluralize(
        registriesAddedCount,
        'registry',
        'registries'
      )}.`;
    }
    return `Please add at least one registry to ${
      mode === 'create' ? 'create' : 'update'
    } access.`;
  }
}
